import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Box
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { GlossaryEntry } from './types';
import { v4 as uuidv4 } from 'uuid';

interface GlossaryFormProps {
  open: boolean;
  entry?: GlossaryEntry;
  onClose: () => void;
  onSave: (entry: GlossaryEntry) => void;
}

const CATEGORIES = [
  { value: 'academic', label: 'Academic' },
  { value: 'behavioral', label: 'Behavioral' },
  { value: 'administrative', label: 'Administrative' },
  { value: 'special_education', label: 'Special Education' },
  { value: 'general', label: 'General' }
];

export const GlossaryForm: React.FC<GlossaryFormProps> = ({
  open,
  entry,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<Omit<GlossaryEntry, 'id' | 'lastModified'>>({
    acronym: '',
    meaning: '',
    explanation: '',
    category: 'general'
  });

  useEffect(() => {
    if (entry) {
      setFormData({
        acronym: entry.acronym,
        meaning: entry.meaning,
        explanation: entry.explanation,
        category: entry.category
      });
    } else {
      setFormData({
        acronym: '',
        meaning: '',
        explanation: '',
        category: 'general'
      });
    }
  }, [entry]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      id: entry?.id || uuidv4(),
      ...formData,
      lastModified: new Date().toISOString()
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box component="h2" sx={{ typography: 'h6', m: 0 }}>
              {entry ? 'Edit Entry' : 'Add New Entry'}
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Acronym"
              value={formData.acronym}
              onChange={(e) => setFormData({ ...formData, acronym: e.target.value })}
              required
              fullWidth
            />

            <TextField
              label="Full Meaning"
              value={formData.meaning}
              onChange={(e) => setFormData({ ...formData, meaning: e.target.value })}
              required
              fullWidth
            />

            <TextField
              label="Explanation"
              value={formData.explanation}
              onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
              multiline
              rows={4}
              required
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                label="Category"
                onChange={(e) => setFormData({ ...formData, category: e.target.value as GlossaryEntry['category'] })}
              >
                {CATEGORIES.map(category => (
                  <MenuItem key={category.value} value={category.value}>
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" color="primary">
            {entry ? 'Save Changes' : 'Add Entry'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};